import { Component, Input, OnInit } from '@angular/core';

import { TranslationService } from '@mirage-projects/cw-commons';
import { CatalogObjectReport } from '@mirage-projects/cw-models';

import { GridColumn } from '../../../../models/grid-column';
import { PaginationData } from '../../../../models/pagination-data';
import { BytesPipe } from '../../../../pipes/bytes.pipe';
import { ReportType } from '../report.component';

const ReportDataType = {
    STORAGE: 'REPORTS.STORAGE',
    STREAMING: 'REPORTS.TRANSFER',
    PROCESSING: 'REPORTS.PROCESSING',
} as const;

/** This components displays the report detail with the data info */
@Component({
    selector: 'cw-report-detail',
    templateUrl: './report-detail.component.html',
    styleUrls: ['./report-detail.component.scss'],
    providers: [BytesPipe],
    standalone: false,
})
export class ReportDetailComponent implements OnInit {
    /**
     *  Report data with information of streaming storage and processing
     */
    @Input() reportDetail!: CatalogObjectReport[];
    /**
     * Caption with the label of the popup
     */
    @Input() caption!: string;

    /**
     * Report type
     */
    @Input() type: ReportType = 'user';

    columns!: GridColumn[];
    data: CatalogObjectReport[] = [];
    paginationData!: PaginationData;
    errorTitle = '';
    description = '';

    constructor(
        private bytesPype: BytesPipe,
        private translationService: TranslationService
    ) {}

    ngOnInit(): void {
        this.columns = this.getColumns();
        if (this.reportDetail) {
            this.paginationData = this.getPagination();
        }
        this.captionInfo();
        this.onChangePage(this.paginationData.currentPage);
    }

    /**
     * update data to show when change the page
     * @param page
     */
    onChangePage(page: number): void {
        this.paginationData.currentPage = page - 1;
        this.data = this.updateDataByPage();
    }

    /**
     * Pending of current page and page size calculate the data to show
     */
    updateDataByPage(): CatalogObjectReport[] {
        const startIndex = this.paginationData?.currentPage * this.paginationData?.pageSize;

        return this.reportDetail?.slice(startIndex, startIndex + this.paginationData?.pageSize);
    }

    /**
     * get the pagination params for the popup
     * @returns {PaginationData}
     */
    getPagination(): PaginationData {
        return {
            currentPage: 1,
            pageSize: 12,
            totalElements: this.reportDetail?.length ?? 0,
        };
    }

    /**
     * Define the column params to show
     * @returns {GridColumn}
     */
    getColumns(): GridColumn[] {
        return [
            { name: 'REPORTS.OBJECT_NAME', field: 'name', isColumnVisible: true, type: 'string' },
            { name: 'REPORTS.OBJECT_TYPE', field: 'type', isColumnVisible: true, type: 'string' },
            {
                name: 'REPORTS.OBJECT_SIZE',
                field: 'size',
                format: (layer) => this.formatSizeValue(layer),
                isColumnVisible: true,
                type: 'string',
            },
            { name: 'REPORTS.CREATED_TIME', field: 'createdAt', isColumnVisible: true, type: 'date' },
        ];
    }

    /**
     *  Pending the caption value initialize the description and the error title
     */
    captionInfo(): void {
        switch (this.caption) {
            case ReportDataType.PROCESSING:
                this.description = 'REPORTS.PROCESSING_OBJECTS';
                this.errorTitle = this.type === 'user' ? 'REPORTS.NO_PROCESSING_OBJECTS' : 'REPORTS.NO_PROCESSING_OBJECTS_COMPANY';
                break;
            case ReportDataType.STREAMING:
                this.description = 'REPORTS.TRANSFER_OBJECTS';
                this.errorTitle = this.type === 'user' ? 'REPORTS.NO_TRANSFER_OBJECTS' : 'REPORTS.NO_TRANSFER_OBJECTS_COMPANY';
                break;
            case ReportDataType.STORAGE:
                this.description = 'REPORTS.STORAGE_OBJECTS';
                this.errorTitle = this.type === 'user' ? 'REPORTS.NO_STORAGE_OBJECTS' : 'REPORTS.NO_STORAGE_OBJECTS_COMPANY';
                break;
            default:
                this.description = '--';
                this.errorTitle = '--';
                break;
        }
    }

    private formatSizeValue(layer: CatalogObjectReport): string {
        if (this.caption === ReportDataType.PROCESSING) {
            return `${layer.size} ${this.translationService.translate('REPORTS.CREDITS')}`;
        }

        return this.bytesPype.transform(layer.size) ?? '-';
    }
}
